<template>
  <div class="professional-insurances">
    <div class="stenci-group stenci-alert text-center">
      <h6 class="stenci-title text-secondary">
        Controle de planos de saúde por profissional
      </h6>

      <div class="form-group">
        Defina em quais planos de saúde este profissional poderá realizar atendimentos.
      </div>

      <div class="form-group">
        <strong>Atenção:</strong> Ao vincular este profissional a um plano de saúde que
        não possui restrição por profissional,<br> esse plano passará a atender apenas
        os profissionais vinculados. Isso significa que:
      </div>

      <ol>
        <li>Se <strong>nenhum plano</strong> estiver selecionado, este profissional poderá
          atender pacientes de <strong>todos</strong> os convênios.</li>
        <li>Ao <strong>vincular um ou mais planos</strong>, o atendimento desses planos
          ficará restrito exclusivamente aos profissionais vinculados a eles.</li>
        <li>Outros profissionais que também atendam por esses planos
          <strong>deverão ser vinculados manualmente</strong>.</li>
      </ol>

      <div class="mb-2" v-if="!isNew">
        <button class="btn btn-secondary btn-icon btn-icon-left"
                :disabled="saving || deleting"
                @click="addInsurance">
          <fa-icon :icon="['fal', 'plus']" />Adicionar plano de saúde
        </button>
      </div>
      <div class="text-bold text-error" v-else>Para adicionar, primeiro salve o cadastro!</div>
    </div>

    <div class="loading loading-lg" v-if="loading || saving" />

    <template v-else>
      <div class="empty mt-2" v-if="data.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h5">Lista de planos de saúde</p>
        <p class="empty-subtitle">
          Nenhum plano de saúde adicionado, clique no botão abaixo para adicionar.
        </p>
      </div>
      <table class="table table-hover" v-else>
        <thead>
        <tr>
          <th>#</th>
          <th>Nome do plano de saúde</th>
          <th style="width: 200px">Código na operadora</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, i) in data.items" :key="i">
          <td>{{ i + 1 }}</td>
          <td>{{ item.customName }}</td>
          <td>
            <input
              type="text"
              class="form-input input-sm"
              v-model="item.contractCode"
              :disabled="saving || deleting"
              @change="updateInsurance(item)"
              v-mask="'00000000000000000000'"
              placeholder="000000"
            />
          </td>
          <td class="text-right">
            <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                    :class="{loading: deleting && idx === i}"
                    :disabled="saving || deleting"
                    data-tooltip="Excluir"
                    @click="removeInsurance(item)">
              <fa-icon :icon="['fal', 'times']" />
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </template>

    <find-insurance-plan-modal
      v-if="findInsurancePlanModal.show"
      @selected="selectedInsurances"
      @close="findInsurancePlanModal.show = false"
    />
  </div>
</template>

<script>
import { customName } from '@/helpers/insurance';
import FindInsurancePlanModal from './modals/FindInsurancePlanModal.vue';

export default {
  props: {
    isNew: {
      type: Boolean,
      default: true,
    },
    professionalId: {
      type: String,
      required: true,
    },
  },
  components: {
    FindInsurancePlanModal,
  },
  data() {
    return {
      idx: null,
      loading: false,
      saving: false,
      deleting: false,
      updating: false,
      findInsurancePlanModal: {
        show: false,
      },
      data: {
        items: [],
        hasMore: false,
        limit: 0,
        offset: 0,
      },
    };
  },
  async mounted() {
    if (!this.isNew) {
      this.init();
    }
  },
  methods: {
    init() {
      this.loadInsuranceProfessionals();
    },
    loadInsuranceProfessionals() {
      this.loading = true;
      const { professionalId } = this;

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
      };

      return this.$http
        .get(`/professionals/${professionalId}/insurance-plans`, { params })
        .then(({ data }) => {
          this.data.items = data.items.map(item => ({
            id: item.insurance.id,
            customName: customName(item.insurance),
            name: item.insurance.name,
            plan: {
              id: item.insurance.plan.id,
              name: item.insurance.plan.name,
            },
            contractCode: item.contractCode,
          }));
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        }).finally(() => {
          this.loading = false;
        });
    },
    addInsurance() {
      this.findInsurancePlanModal.show = true;
    },
    selectedInsurances(insurances) {
      if (this.saving || this.deleting) {
        return null;
      }

      this.saving = true;
      this.findInsurancePlanModal.show = false;

      const savedPlanIds = this.data.items.map(item => item.plan.id);
      insurances = insurances.filter(item => !savedPlanIds.includes(item.plan.id));

      insurances.forEach((insurance) => {
        insurance.contractCode = null;
      });

      if (insurances.length === 0) {
        this.saving = false;
        return null;
      }

      const { professionalId } = this;

      return this.$http
        .post(`/professionals/${professionalId}/insurance-plans`, { insurances })
        .then(() => {
          this.data.items = [...this.data.items, ...insurances];
          this.$toast.success('Planos de saúde adicionados com sucesso!');
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        }).finally(() => {
          this.saving = false;
        });
    },
    updateInsurance(insurance) {
      if (this.saving || this.deleting || this.updating) {
        return null;
      }

      this.updating = true;

      const { professionalId } = this;

      const data = {
        insurances: [insurance],
      };

      return this.$http
        .post(`/professionals/${professionalId}/insurance-plans`, data)
        .then(() => {
          this.$toast.success('Registro atualizado!');
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        }).finally(() => {
          this.updating = false;
          this.findInsurancePlanModal.show = false;
        });
    },
    removeInsurance(item) {
      if (this.deleting) {
        return;
      }

      this.idx = this.data.items.indexOf(item);
      this.deleting = true;

      const { professionalId } = this;

      this.$http
        .delete(`/professionals/${professionalId}/insurances/${item.id}/plans/${item.plan.id}`)
        .then(() => {
          this.data.items.splice(this.idx, 1);
        })
        .catch(() => {
          this.$toast.error('Erro ao excluir o plano de saúde!');
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>
